import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Plus, Kanban, Trash2 } from "lucide-react"
import { useState } from "react"
import { Button, Card, Skeleton, toast } from "@aix/ui"
import { api } from "~/utils/api"
import { BoardConfigModal } from "~/components/MediaSpace/BoardConfigModal"

function AllBoardsPage() {
  const navigate = useNavigate()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const utils = api.useUtils()

  const { data: boards, isLoading } = api.mediaSpace.getBoards.useQuery()

  const deleteMutation = api.mediaSpace.deleteBoard.useMutation({
    onSuccess: () => {
      toast.success("Board deleted")
      void utils.mediaSpace.getBoards.invalidate()
    },
    onError: (err) => toast.error(err.message),
  })

  if (isLoading) {
    return (
      <div className="p-6 grid grid-cols-2 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-40 rounded-lg" />
        ))}
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold">All Boards</h2>
        <Button size="sm" onClick={() => setShowCreateModal(true)}>
          <Plus size={14} className="mr-1.5" />
          New Board
        </Button>
      </div>

      {(!boards || boards.length === 0) && (
        <div className="text-center py-16">
          <Kanban size={32} className="text-muted-foreground mx-auto mb-3" />
          <h3 className="text-base font-semibold mb-1">No boards yet</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Create a board to organize your assets with custom columns.
          </p>
          <Button onClick={() => setShowCreateModal(true)}>Create Board</Button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {boards?.map((board) => (
          <Card
            key={board.id}
            className="cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all p-4 group"
            onClick={() => navigate({ to: "/media-space/board/$boardId", params: { boardId: board.id } })}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold truncate">{board.name}</h3>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-destructive"
                onClick={(e) => {
                  e.stopPropagation()
                  if (confirm("Delete this board?")) deleteMutation.mutate({ boardId: board.id })
                }}
              >
                <Trash2 size={12} />
              </Button>
            </div>

            {/* Column preview */}
            <div className="flex gap-1 mb-3">
              {board.columns.map((col) => (
                <div key={col.id} className="flex-1 h-1.5 rounded-full" style={{ backgroundColor: col.color }} />
              ))}
            </div>

            <div className="flex items-center justify-between text-[10px] text-muted-foreground">
              <span>{board.columns.length} columns</span>
              <span>{board._count?.assets ?? 0} assets</span>
            </div>
          </Card>
        ))}
      </div>

      <BoardConfigModal open={showCreateModal} onOpenChange={setShowCreateModal} mode="create" />
    </div>
  )
}

export const Route = createFileRoute("/media-space/boards")({
  component: AllBoardsPage,
})
